/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Backdrop, Grid, MenuItem, Switch, Typography } from '@mui/material';
import { addIntegration } from '@sentry/react';
import React from 'react';
import { ErrorBoundary, ToolContainerDraggable } from '@opengeoweb/shared';
import { useSentryTranslation } from '../../utils/i18n';

interface UserMenuItemSentryRecordingProps {
  recordButtonEnabled?: boolean;
  setRecordButtonEnabled?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const UserMenuItemSentryRecording: React.FC<
  UserMenuItemSentryRecordingProps
> = ({ recordButtonEnabled, setRecordButtonEnabled }) => {
  const [open, setOpen] = React.useState(false);
  const [canvasIntegrationEnabled, setCanvasIntegrationEnabled] =
    React.useState(false);
  const [htmlRecordingEnabled, setHTMLRecordingEnabled] = React.useState(false);
  const { t } = useSentryTranslation();
  const onClose = (): void => {
    setOpen(false);
  };
  const onOpen = (): void => {
    setOpen(true);
  };

  const enableHTMLRecording = async (): Promise<void> => {
    if (recordButtonEnabled) {
      return;
    }
    if (canvasIntegrationEnabled) {
      return;
    }
    try {
      const { replayIntegration } = await import('@sentry/react');
      addIntegration(
        replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      );
      setRecordButtonEnabled?.(!recordButtonEnabled);
      setHTMLRecordingEnabled(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  const enableHTMLAndCanvasRecording = async (): Promise<void> => {
    if (recordButtonEnabled) {
      return;
    }
    try {
      const { replayIntegration, replayCanvasIntegration } = await import(
        '@sentry/react'
      );
      addIntegration(
        replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      );
      addIntegration(replayCanvasIntegration());
      setCanvasIntegrationEnabled(!canvasIntegrationEnabled);
      setRecordButtonEnabled?.(!recordButtonEnabled);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  return (
    <ErrorBoundary>
      <MenuItem data-testid="sentryRecording-btn" divider onClick={onOpen}>
        <Grid container>
          <Grid>{t('sentry-recording')}</Grid>
        </Grid>
      </MenuItem>
      <Backdrop
        data-testid="sentryRecording-content"
        open={open}
        sx={{
          zIndex: 1,
        }}
      >
        <ToolContainerDraggable
          bounds="parent"
          isOpen={true}
          minHeight={300}
          minWidth={500}
          onClose={onClose}
          startSize={{ width: 700 }}
          title={t('sentry-recording')}
        >
          <Grid
            container
            spacing={4}
            sx={{ paddingLeft: '70px', paddingRight: '70px' }}
          >
            <Grid
              container
              justifyContent="center"
              sx={{ textAlign: 'center' }}
            >
              <Typography>{t('sentry-information')}</Typography>
            </Grid>

            <Grid
              container
              justifyContent="center"
              sx={{ textAlign: 'center' }}
            >
              <Typography>{t('sentry-enable-message')}</Typography>
            </Grid>

            <Grid alignItems="center" container direction="column">
              <Switch
                checked={
                  recordButtonEnabled ? !canvasIntegrationEnabled : undefined
                }
                disabled={htmlRecordingEnabled}
                onChange={enableHTMLRecording}
              />
              {recordButtonEnabled && !canvasIntegrationEnabled ? (
                <Typography> {t('sentry-disable')}</Typography>
              ) : (
                <Typography> {t('sentry-enable')}</Typography>
              )}
            </Grid>

            <Grid alignItems="center" container direction="column">
              {/* TODO - We need to build a fetching mechanism that will handle both cases of fetching images with CORS headers of "Access-Control-Allow-Origin: *" . After this we can enable the Canvas recording */}
              <Switch
                checked={canvasIntegrationEnabled}
                disabled={true}
                onChange={enableHTMLAndCanvasRecording}
              />
              {canvasIntegrationEnabled ? (
                <Typography>{t('sentry-disable-both')}</Typography>
              ) : (
                <>
                  <Typography sx={{ color: 'gray' }}>
                    {t('sentry-enable-both')}
                  </Typography>
                  <Typography sx={{ color: 'gray' }} textAlign="center">
                    ({t('sentry-cors-wait')})
                  </Typography>
                </>
              )}
            </Grid>
            <Grid container justifyContent="center">
              <Typography sx={{ paddingTop: '20px', textAlign: 'center' }}>
                {t('sentry-disclaimer')}
              </Typography>
            </Grid>
          </Grid>
        </ToolContainerDraggable>
      </Backdrop>
    </ErrorBoundary>
  );
};

export default UserMenuItemSentryRecording;
